import { ref, readonly } from 'vue';
import { useChartStore } from '@/stores/chartStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useChartNotesV2 } from '@/composables/useChartNotesV2';
import { useSymbolInformation } from '@/composables/useSymbolInformation';
import { useExchangeHours } from '@/composables/useExchangeHours';
import { useModals } from '@/composables/useModals';
import { useAccountLevelsMaximumLimits } from '@/composables/useAccountLevelsMaximumLimits';

const isAutoRefreshEnabled = ref(false);
const autoRefreshLoopTimeoutId = ref<number>(-1);
let wasLastChartRefreshCanceledDueToInspectorBeingInUse = false;
let isRefreshChartCallbackQueued = false;
let hasInitialized = false;

async function autoRefreshLoop() {
  const chartStore = useChartStore();
  const layoutStore = useLayoutStore();
  const { isLoggedIn } = useAuthentication();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { getSymbolInformation, isSymbolBeingViewedNotFound, isSymbolBeingViewedAnEODSymbolOnAnIntradayChart } = useSymbolInformation();
  const { isExchangeOpen }  = useExchangeHours();
  const { autoRefreshIntervalSeconds } = useAccountLevelsMaximumLimits();

  window.clearTimeout(autoRefreshLoopTimeoutId.value);

  autoRefreshLoopTimeoutId.value = -1;

  if (
    !isLoggedIn.value ||
    !isAutoRefreshEnabled.value ||
    !layoutStore.hasChartImageLoadAttemptCompleted ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value ||
    isSymbolBeingViewedNotFound.value ||
    isSymbolBeingViewedAnEODSymbolOnAnIntradayChart.value ||
    autoRefreshIntervalSeconds.value <= 0
  ) {
    autoRefreshLoopTimeoutId.value = window.setTimeout(async () => {
      await autoRefreshLoop();
    }, autoRefreshIntervalSeconds.value * 1000);
    return;
  }

  const symbolCopy = chartStore.symbol;
  let symbolInformation;

  try {
    symbolInformation = await getSymbolInformation(symbolCopy);
  } catch (e) {
    window.sc3WorkbenchLogger.debug(`Error while getting symbol information for symbol: ${symbolCopy} during auto-refresh-loop iteration.`, e);
  }

  if (
    !symbolInformation ||
    !isLoggedIn.value ||
    !isAutoRefreshEnabled.value ||
    !layoutStore.hasChartImageLoadAttemptCompleted ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value ||
    isSymbolBeingViewedNotFound.value ||
    isSymbolBeingViewedAnEODSymbolOnAnIntradayChart.value ||
    autoRefreshIntervalSeconds.value <= 0
  ) {
    if (autoRefreshLoopTimeoutId.value > -1) {
      return;
    }

    autoRefreshLoopTimeoutId.value = window.setTimeout(async () => {
      await autoRefreshLoop();
    }, autoRefreshIntervalSeconds.value * 1000);
    return;
  }

  const considerExtendedHours = chartStore.extendedHours && symbolInformation.marketRegion === 'US';
  const isExchangeCurrentlyOpen = await isExchangeOpen(symbolInformation.exchange, considerExtendedHours);

  if (
    isExchangeCurrentlyOpen &&
    isLoggedIn.value &&
    isAutoRefreshEnabled.value &&
    layoutStore.hasChartImageLoadAttemptCompleted &&
    !isActivatingAnnotationMode.value &&
    !isAnnotating.value &&
    !isSymbolBeingViewedNotFound.value &&
    !isSymbolBeingViewedAnEODSymbolOnAnIntradayChart.value &&
    autoRefreshIntervalSeconds.value > 0
  ) {
    refreshChart();
  }

  if (autoRefreshLoopTimeoutId.value > -1) {
    return;
  }

  autoRefreshLoopTimeoutId.value = window.setTimeout(async () => {
    await autoRefreshLoop();
  }, autoRefreshIntervalSeconds.value * 1000);
}

function updateIsAutoRefreshEnabled(isAutoRefreshNowEnabled: boolean) {
  const { isLoggedIn } = useAuthentication();
  const { launchCreateAccountModal } = useModals();

  if (!isLoggedIn.value) {
    launchCreateAccountModal();
    return;
  }

  isAutoRefreshEnabled.value = isAutoRefreshNowEnabled;

  refreshChart();
}

function refreshChart() {
  const layoutStore = useLayoutStore();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { isSymbolBeingViewedNotFound, isSymbolBeingViewedAnEODSymbolOnAnIntradayChart } = useSymbolInformation();

  wasLastChartRefreshCanceledDueToInspectorBeingInUse = false;

  if (
    !layoutStore.hasChartImageLoadAttemptCompleted ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value ||
    isSymbolBeingViewedNotFound.value ||
    isSymbolBeingViewedAnEODSymbolOnAnIntradayChart.value
  ) {
    return;
  }

  if (!layoutStore.isInspectorInUse) {
    layoutStore.$patch({ chartImageUrlStringRandomNumber: Date.now() });
    return;
  }

  wasLastChartRefreshCanceledDueToInspectorBeingInUse = true;

  if (isRefreshChartCallbackQueued) {
    return;
  }

  window.setTimeout(() => {
    isRefreshChartCallbackQueued = false;

    if (!wasLastChartRefreshCanceledDueToInspectorBeingInUse) {
      return;
    }

    refreshChart();
  }, 1000);

  isRefreshChartCallbackQueued = true;
}

async function init() {
  const { isLoggedIn } = useAuthentication();

  if (hasInitialized) {
    return;
  }

  hasInitialized = true;

  if (isLoggedIn.value) {
    isAutoRefreshEnabled.value = true;
  }

  await autoRefreshLoop();
}

export function useAutoRefresh() {
  const { addMemberAuthenticationCallback } = useAuthentication();

  addMemberAuthenticationCallback(init);

  return {
    isAutoRefreshEnabled: readonly(isAutoRefreshEnabled),
    updateIsAutoRefreshEnabled,
    refreshChart
  };
}
